<script setup lang="ts">
import { Eye, Loader2, Plus } from 'lucide-vue-next'
import axios from 'axios'
import hash from 'object-hash'

import EmptyState from '@/components/EmptyState.vue'
import LoyaltyPointExpirationConfirmModal from './components/LoyaltyPointExpirationConfirmModal.vue'
import LoyaltyPointsSettings from './components/LoyaltyPointsSettings.vue'
import LoyaltyPreviewModal from './components/LoyaltyPreviewModal.vue'
import LoyaltyRewardList from './components/LoyaltyRewardList.vue'
import LoyaltySettings from './components/LoyaltySettings.vue'
import useLoyaltyPageTabs from './composables/loyaltyPageTabs'

import type { MerchantConfigs, MerchantTexts } from '@/types/types'

const { checkHasActiveIntegration } = useIntegration()
const { user, stores, isSupportConnection } = useLocalStorage()
const { getMerchantTexts, patchMerchantTexts } = useMerchantTexts()
const { getMerchantConfigs, putMerchantConfigs } = useMerchantConfigs()
const { selectedStoreFilter } = useStore()
const { t } = useI18n()
const { loyaltyPointsConfigFields, loyaltyNotificationsConfigFields } = useLoyalty()
const { fetchLoyaltyProgramRewardsPaged, pagination } = useReward()
const notification = useNotification()
const { tabs, currentTab } = useLoyaltyPageTabs()
const route = useRoute()
const { send } = useEvent()
const { hasPermissionBySlug } = useAccess()
const { merchantNotificationConfigs } = useMerchantNotificationConfigs()
const storeFilter = ref<number[]>(selectedStoreFilter.value.length ? [...selectedStoreFilter.value] : [])
const rewardListRef = ref<InstanceType<typeof LoyaltyRewardList> | null>(null)
const hasAnActiveIntegration = ref(false)
const isLoaded = ref(false)
const isPreviewModalOpen = ref(false)
const isSubmitting = ref(false)
const merchantConfigs = ref<MerchantConfigs>({})
const merchantTexts = ref<MerchantTexts>(null)
const rewards = ref([])
const isRefreshing = ref(false)
const hasRewards = ref(false)
const hasMessageContentError = ref(false)
const pointExpirationType = ref<'point_relative' | 'after_registration' | 'absolute_date' | null>(null)
const pointExpirationUnit = ref<'day' | 'month' | 'year' | null>(null)
const pointExpirationValue = ref<number | string | null>(null)
// Set when the merchant chose to keep points earned before the rule was enabled.
const pointExpirationStartsAt = ref<string | null>(null)

const routeQuery = computed(() => route.query)

const showRewardsActions = computed(
  () => currentTab.value === 'rewards' && (hasRewards.value || Boolean(route.query.activeTab))
)

const pageTitle = {
  title: 'pages.loyaltyPage.title'
}

const templateId = computed(() => route.query?.templateId)

const hasCompatibleVoucherIntegration = computed(() => {
  return Boolean((user.value.merchant as any)?.hasCompatibleVoucherIntegration)
})

const handlePreviewModalState = () => {
  isPreviewModalOpen.value = !isPreviewModalOpen.value
}

const handleShowAll = () => {
  updateRewardList()
}

watch(storeFilter, async (newValue) => {
  selectedStoreFilter.value = [...newValue]
  await updateRewardList(1)
})

const updateRewardList = async (page: number = pagination.value.currentPage) => {
  const isAllStoresSelected = storeFilter.value.length === stores.value.length
  const storeIds = storeFilter.value.length > 0 && !isAllStoresSelected ? storeFilter.value : undefined
  const response = await fetchLoyaltyProgramRewardsPaged(storeIds, 'created_at', 'DESC', page, 100)

  rewards.value = response.data || []
  pagination.value = {
    currentPage: response.meta.current_page,
    lastPage: response.meta.last_page,
    total: response.meta.total,
    pageSize: pagination.value.pageSize
  }
}

const handlePageChange = async (page: number) => {
  isRefreshing.value = true
  await updateRewardList(page)
  isRefreshing.value = false
}

const merchantConfigPayload = computed(() => createMerchantConfigPayload())
const merchantTextsPayload = computed(() => createMerchantTextsPayload())
const haveMerchantConfigsModifications = computed(
  () => hash(merchantConfigPayload.value) !== initialMerchantConfigsHash.value
)
const haveMerchantTextsModifications = computed(
  () => hash(merchantTextsPayload.value) !== initialMerchantTextsHash.value
)

const initialPointExpirationType = ref<'point_relative' | 'after_registration' | 'absolute_date' | null>(null)
const initialPointExpirationUnit = ref<'day' | 'month' | 'year' | null>(null)
const initialPointExpirationValue = ref<number | string | null>(null)
const isExpirationConfirmOpen = ref(false)

const currentPointExpirationType = computed(() => toNullableExpirationType(pointExpirationType.value))
const currentPointExpirationUnit = computed(() => toNullableExpirationUnit(pointExpirationUnit.value))
const currentPointExpirationValue = computed(() =>
  toNullableExpirationValue(pointExpirationValue.value, currentPointExpirationType.value)
)

const hasPointExpirationModifications = computed(() => {
  return (
    currentPointExpirationType.value !== initialPointExpirationType.value ||
    currentPointExpirationUnit.value !== initialPointExpirationUnit.value ||
    currentPointExpirationValue.value !== initialPointExpirationValue.value
  )
})

const needsExpirationDisclaimer = computed(() => {
  if (!hasPointExpirationModifications.value) return false
  return currentPointExpirationType.value != null && currentPointExpirationValue.value != null
})

const snapshotPointExpiration = () => {
  initialPointExpirationType.value = currentPointExpirationType.value
  initialPointExpirationUnit.value = currentPointExpirationUnit.value
  initialPointExpirationValue.value = currentPointExpirationValue.value
}

const onSaveClick = () => {
  if (!haveMerchantConfigsModifications.value && !haveMerchantTextsModifications.value) {
    notification.create('error', {
      text: t('pages.loyaltyPage.notifications.noChanges')
    })
    return
  }

  if (needsExpirationDisclaimer.value) {
    isExpirationConfirmOpen.value = true
    return
  }

  handleSubmit()
}

// applyToExistingPoints is only provided when the expiration rule was confirmed through the modal.
const handleSubmit = async (applyToExistingPoints?: boolean) => {
  isSubmitting.value = true

  try {
    if (!haveMerchantConfigsModifications.value && !haveMerchantTextsModifications.value) {
      notification.create('error', {
        text: t('pages.loyaltyPage.notifications.noChanges')
      })
      return
    }
    if (haveMerchantConfigsModifications.value) {
      await putMerchantConfigs(
        applyToExistingPoints === undefined
          ? merchantConfigPayload.value
          : { ...merchantConfigPayload.value, pointExpirationRetroactive: applyToExistingPoints }
      )
      initialMerchantConfigsHash.value = hash(merchantConfigPayload.value)
      snapshotPointExpiration()
      if (currentPointExpirationType.value == null) {
        pointExpirationStartsAt.value = null
      } else if (applyToExistingPoints !== undefined) {
        pointExpirationStartsAt.value = applyToExistingPoints ? null : new Date().toISOString()
      }
    }
    if (haveMerchantTextsModifications.value) {
      await patchMerchantTexts(merchantTextsPayload.value)
      initialMerchantTextsHash.value = hash(merchantTextsPayload.value)
    }
  } catch {
    // putMerchantConfigs / patchMerchantTexts already notify on failure
  } finally {
    isSubmitting.value = false
    isExpirationConfirmOpen.value = false
  }
}

const update = async () => {
  await updateRewardList()
}

const setMerchantNotificationDefaultValues = () => {
  const { loyaltyDeliveryOrderMessageChannel } = merchantConfigs.value

  const { integrationAfterOrderMailSubject, integrationAfterOrderMailBody, integrationAfterOrderSms } =
    merchantTexts.value

  const loyaltyFields = loyaltyNotificationsConfigFields.value

  loyaltyFields.hasNotificationAfterOrder.value = !!loyaltyDeliveryOrderMessageChannel

  loyaltyFields.loyaltyDeliveryOrderMessageChannel.value =
    loyaltyFields.loyaltyDeliveryOrderMessageChannel.options.find(
      (option) => option.value === loyaltyDeliveryOrderMessageChannel
    )

  const orderReceivedFields = loyaltyFields.orderReceivedTextsFields

  orderReceivedFields.integrationAfterOrderMailSubject.value = integrationAfterOrderMailSubject
  orderReceivedFields.integrationAfterOrderMailBody.value = integrationAfterOrderMailBody
  orderReceivedFields.integrationAfterOrderSms.value = integrationAfterOrderSms
}

const setMerchantPointsDefaultValues = () => {
  const {
    bonusPoints,
    loyaltyOrderMinimumAmount,
    delayBetweenCheckins,
    checkinPoints,
    loyaltyPointsByCurrencyUnit,
    hasPointsByCurrency,
    hasPointsCanceledOnOrderCancelation,
    pointExpirationType: expirationType,
    pointExpirationUnit: expirationUnit,
    pointExpirationValue: expirationValue,
    pointExpirationStartsAt: expirationStartsAt
  } = merchantConfigs.value
  const globalFields = loyaltyPointsConfigFields.value.globals.fields
  const perVisitFields = loyaltyPointsConfigFields.value.perVisit.fields
  const perAmountSpentFields = loyaltyPointsConfigFields.value.perAmountSpent.fields

  globalFields.bonusPoints.value = bonusPoints
  globalFields.loyaltyOrderMinimumAmount.value = loyaltyOrderMinimumAmount
  globalFields.pointsLabel.value = merchantTexts.value.pointsLabel
  globalFields.hasPointsCanceledOnOrderCancelation.value = hasPointsCanceledOnOrderCancelation

  perVisitFields.delayBetweenCheckins.value = delayBetweenCheckins
  perVisitFields.checkinPoints.value = checkinPoints

  loyaltyPointsConfigFields.value.perAmountSpent.toggleValue = hasPointsByCurrency
  perAmountSpentFields.loyaltyPointsByCurrencyUnit.value = loyaltyPointsByCurrencyUnit
  pointExpirationType.value = toNullableExpirationType(expirationType ?? null)
  pointExpirationUnit.value = toNullableExpirationUnit(expirationUnit ?? null)
  pointExpirationValue.value = toNullableExpirationValue(expirationValue ?? null, expirationType ?? null)
  pointExpirationStartsAt.value = expirationStartsAt || null

  if (!hasAnActiveIntegration.value) {
    globalFields.loyaltyOrderMinimumAmount.disabled = true
    globalFields.loyaltyOrderMinimumAmount.tips = {
      link: {
        value: '/integrations',
        isOpenedInNewTab: false
      },
      text: t('pages.loyaltyPage.tabs.points.loyaltyPointsByCurrencyUnitTip')
    }
    perAmountSpentFields.loyaltyPointsByCurrencyUnit.disabled = true
    perAmountSpentFields.loyaltyPointsByCurrencyUnit.tips = {
      link: {
        value: '/integrations',
        isOpenedInNewTab: false
      },
      text: t('pages.loyaltyPage.tabs.points.loyaltyPointsByCurrencyUnitTip')
    }
    loyaltyPointsConfigFields.value.perAmountSpent.toggleValue = false
    loyaltyPointsConfigFields.value.perAmountSpent.toggleDisabled = true
    loyaltyPointsConfigFields.value.perAmountSpent.toggleTips = {
      link: {
        value: '/integrations',
        isOpenedInNewTab: false
      },
      text: t('pages.loyaltyPage.tabs.points.loyaltyPointsByCurrencyUnitTip')
    }
  }
}

const initialMerchantTextsHash = ref('')
const initialMerchantConfigsHash = ref('')
const initialMerchantNotificationConfigsHash = ref('')

const fetchAll = async () => {
  merchantTexts.value = await getMerchantTexts()
  const { integrationAfterOrderSms, integrationAfterOrderMailBody, integrationAfterOrderMailSubject } =
    merchantTexts.value
  initialMerchantTextsHash.value = hash({
    integrationAfterOrderSms,
    integrationAfterOrderMailBody,
    integrationAfterOrderMailSubject
  })
  merchantConfigs.value = await getMerchantConfigs()
  const {
    bonusPoints,
    loyaltyOrderMinimumAmount,
    delayBetweenCheckins,
    checkinPoints,
    loyaltyPointsByCurrencyUnit,
    loyaltyDeliveryOrderMessageChannel,
    hasPointsByCurrency,
    hasPointsCanceledOnOrderCancelation,
    pointExpirationType: expirationType,
    pointExpirationUnit: expirationUnit,
    pointExpirationValue: expirationValue
  } = merchantConfigs.value
  const initialConfigsHash: any = {
    bonusPoints,
    loyaltyOrderMinimumAmount,
    delayBetweenCheckins,
    checkinPoints,
    loyaltyPointsByCurrencyUnit,
    loyaltyDeliveryOrderMessageChannel,
    hasPointsByCurrency,
    pointExpirationType: toNullableExpirationType(expirationType ?? null),
    pointExpirationUnit: toNullableExpirationUnit(expirationUnit ?? null),
    pointExpirationValue: toNullableExpirationValue(expirationValue ?? null, expirationType ?? null)
  }

  // Only include hasPointsCanceledOnOrderCancelation in hash if it's a support connection
  if (isSupportConnection.value) {
    initialConfigsHash.hasPointsCanceledOnOrderCancelation = hasPointsCanceledOnOrderCancelation
  }

  initialMerchantConfigsHash.value = hash(initialConfigsHash)
  isLoaded.value = false
  if (templateId.value) {
    send('segment', 'loyalty.template:create:clicked', {
      templateId: Number(templateId.value),
      merchantId: user.value.merchant.publicId,
      module: 'loyalty'
    })
  }
  hasAnActiveIntegration.value = await checkHasActiveIntegration()
  await updateRewardList()
  setMerchantNotificationDefaultValues()
  setMerchantPointsDefaultValues()
  snapshotPointExpiration()
  isLoaded.value = true
}

const redirectFromSuccessPage = () => {
  if (user.value.merchant.isSelfServe && routeQuery.value.isLaunchLoyaltyProgram) {
    send('segment', 'rewards:launch:clicked', {
      isFromSuccessPage: true
    })
  }
}
redirectFromSuccessPage()

const toStringOrZero = (value: any): string => {
  if (value === null || value === undefined) {
    return '0'
  }
  return value.toString()
}

const toNullableExpirationType = (
  value: string | null | undefined
): 'point_relative' | 'after_registration' | 'absolute_date' | null =>
  value === 'point_relative' || value === 'after_registration' || value === 'absolute_date' ? value : null

const toNullableExpirationUnit = (value: string | null | undefined): 'day' | 'month' | 'year' | null =>
  value === 'day' || value === 'month' || value === 'year' ? value : null

const toNullableExpirationValue = (
  value: number | string | null | undefined,
  type: string | null | undefined
): number | string | null => {
  if (value === null || value === undefined || value === '') {
    return null
  }
  if (type === 'absolute_date') {
    return /^(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/.test(String(value)) ? String(value) : null
  }
  const num = Number(value)
  return Number.isNaN(num) ? null : num
}

watch(
  () => templateId.value,
  () => {
    if (templateId.value) {
      fetchAll()
    }
  }
)

const createMerchantConfigPayload = () => {
  const {
    globals: {
      fields: { bonusPoints, loyaltyOrderMinimumAmount, hasPointsCanceledOnOrderCancelation }
    },
    perVisit: {
      fields: { delayBetweenCheckins, checkinPoints }
    },
    perAmountSpent: {
      toggleValue,
      fields: { loyaltyPointsByCurrencyUnit }
    }
  } = loyaltyPointsConfigFields.value

  const { loyaltyDeliveryOrderMessageChannel, hasNotificationAfterOrder } = loyaltyNotificationsConfigFields.value

  const updatedMerchantConfigsValue: any = {
    bonusPoints: toStringOrZero(bonusPoints.value),
    loyaltyOrderMinimumAmount: toStringOrZero(loyaltyOrderMinimumAmount.value),
    delayBetweenCheckins: toStringOrZero(delayBetweenCheckins.value),
    checkinPoints: toStringOrZero(checkinPoints.value),
    loyaltyPointsByCurrencyUnit: toStringOrZero(loyaltyPointsByCurrencyUnit.value),
    hasPointsByCurrency: toggleValue && hasAnActiveIntegration.value,
    pointExpirationType: toNullableExpirationType(pointExpirationType.value),
    pointExpirationUnit: toNullableExpirationUnit(pointExpirationUnit.value),
    pointExpirationValue: toNullableExpirationValue(pointExpirationValue.value, pointExpirationType.value),
    loyaltyDeliveryOrderMessageChannel: hasNotificationAfterOrder.value
      ? loyaltyDeliveryOrderMessageChannel.value != null
        ? toStringOrZero(loyaltyDeliveryOrderMessageChannel.value.value)
        : null
      : null
  }

  // Only include hasPointsCanceledOnOrderCancelation if it's a support connection
  if (isSupportConnection.value) {
    updatedMerchantConfigsValue.hasPointsCanceledOnOrderCancelation = Boolean(
      hasPointsCanceledOnOrderCancelation.value ?? false
    )
  }

  return updatedMerchantConfigsValue
}

const createMerchantTextsPayload = () => {
  const {
    orderReceivedTextsFields: {
      integrationAfterOrderSms,
      integrationAfterOrderMailBody,
      integrationAfterOrderMailSubject
    }
  } = loyaltyNotificationsConfigFields.value
  const globalFields = loyaltyPointsConfigFields.value.globals.fields

  return {
    integrationAfterOrderSms: integrationAfterOrderSms.value as string,
    integrationAfterOrderMailBody: integrationAfterOrderMailBody.value as string,
    integrationAfterOrderMailSubject: integrationAfterOrderMailSubject.value as string,
    pointsLabel: String(globalFields.pointsLabel.value)
  }
}

const isNewCustomerAutopilotActive = ref(false)

const getNewCustomerAutopilot = async () => {
  try {
    const { data } = await axios.get('/autopilots')
    isNewCustomerAutopilotActive.value =
      data.data?.find((autopilot) => autopilot.trigger === 'CustomerNew')?.isActive ?? false
  } catch (error) {
    isNewCustomerAutopilotActive.value = false
    throw error
  }
}

const setMerchantNotificationConfigs = () => {
  if (merchantNotificationConfigs?.value) {
    initialMerchantNotificationConfigsHash.value = hash(merchantNotificationConfigs.value)
  }
}

watch(routeQuery, () => {
  redirectFromSuccessPage()
})

onMounted(async () => {
  await setMerchantNotificationConfigs()
  getNewCustomerAutopilot()
  await fetchAll()
  hasRewards.value = rewards.value.length > 0
})
</script>
<template>
  <div>
    <div v-if="isLoaded">
      <div>
        <TheListHeader :title="t(pageTitle.title)">
          <template #actions>
            <Button
              v-if="currentTab !== 'rewards'"
              variant="default-primary"
              :disabled="!hasPermissionBySlug('reward.update') || hasMessageContentError || isSubmitting"
              @click="onSaveClick"
            >
              <Loader2 v-if="isSubmitting" class="animate-spin" />
              {{ t('pages.settings.merchantForm.button.save') }}
            </Button>

            <template v-if="showRewardsActions">
              <Button variant="outline" @click="handlePreviewModalState">
                <Eye />
                {{ t('pages.loyaltyPage.preview') }}
              </Button>
              <Button variant="default-primary" @click="rewardListRef?.openSelectExistingRewardModal()">
                <Plus />
                {{ t('pages.loyaltyPage.buttons.newReward') }}
              </Button>
            </template>
          </template>
        </TheListHeader>

        <LoyaltyPointExpirationConfirmModal
          :is-open="isExpirationConfirmOpen"
          :is-processing="isSubmitting"
          :type="currentPointExpirationType"
          :unit="currentPointExpirationUnit"
          :value="currentPointExpirationValue"
          @close="isExpirationConfirmOpen = false"
          @confirm="handleSubmit"
        />

        <BaseModal v-if="currentTab === 'rewards'" :is-open="isPreviewModalOpen" @close="handlePreviewModalState">
          <template #customHeadingTitle>
            <div class="relative flex h-fit w-full items-center gap-4 space-y-0.5 text-foreground">
              <BaseIcon name="HeroEyeIconSolid" class="h-5 w-5" />
              <h3 class="text-base font-semibold">{{ t('pages.loyaltyPage.preview') }}</h3>
            </div>
          </template>
          <LoyaltyPreviewModal :rewards="rewards" />
        </BaseModal>
        <div v-if="hasRewards || route.query.activeTab">
          <div class="mb-4 px-6 sm:px-12">
            <BaseTab v-model="currentTab" :tabs="tabs" />
          </div>
          <div v-if="currentTab === 'rewards'" class="mb-6 px-6 sm:px-12">
            <MultiSelectStores v-model="storeFilter" has-label />
          </div>
          <div class="px-6 pb-6 sm:px-12 sm:pb-12">
            <LoyaltyRewardList
              v-show="currentTab === 'rewards'"
              ref="rewardListRef"
              :stores="stores"
              :rewards="rewards"
              :pagination="pagination.lastPage > 1 ? pagination : null"
              :is-loading="isRefreshing"
              :has-compatible-voucher-integration="hasCompatibleVoucherIntegration"
              @update-reward="update"
              @handle-show-all="handleShowAll"
              @update:change-page="handlePageChange"
            />
            <LoyaltyPointsSettings
              v-if="currentTab === 'points'"
              v-model:loyalty-points-config="loyaltyPointsConfigFields"
              v-model:point-expiration-type="pointExpirationType"
              v-model:point-expiration-unit="pointExpirationUnit"
              v-model:point-expiration-value="pointExpirationValue"
              :point-expiration-starts-at="hasPointExpirationModifications ? null : pointExpirationStartsAt"
              :loyalty-points-config-fields="loyaltyPointsConfigFields"
            />
            <LoyaltySettings
              v-if="currentTab === 'settings'"
              :loyalty-notifications-config-fields="loyaltyNotificationsConfigFields"
              @has-message-content-error="hasMessageContentError = $event"
              @update:loyalty-notification-config="loyaltyNotificationsConfigFields = $event"
            />
          </div>
        </div>
        <div v-else class="px-12">
          <EmptyState module="loyalty" :url-params="{ activeTab: tabs[0].value }" />
        </div>
      </div>
    </div>
    <TheLoadingPage v-else />
  </div>
</template>
